// src/pages/AuthPage.js
import React, { useState, useContext, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import API from "../api";
import { UserContext } from "../context/UserContext";
import { useSEO } from "../hooks/useSEO";
import GoalShot from "../components/ui/GoalShot";
import { ScoringChips } from "../components/ScoringGuide";

const fieldClass =
  "h-12 w-full rounded-xl border bg-white px-4 text-[15px] text-ink outline-none transition placeholder:text-gray-400 focus:border-brand focus:ring-2 focus:ring-brand/20";

function AuthPage() {
  useSEO({
    title: "Sign In",
    description: "Sign in to Predict90 or create a new account. Start predicting football match scores and compete with friends.",
    keywords: "login, sign in, register, prediction league, football predictions",
  });
  const { setUser } = useContext(UserContext);
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
  });
  const [validationErrors, setValidationErrors] = useState({
    username: "",
    email: "",
  });
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("verified") === "true") {
      toast.success("Your email was verified! You can now log in.", {
        autoClose: 4000,
        className: "toast-success-custom"
      });
      setIsLogin(true);
    }
  }, [searchParams]);

  useEffect(() => {
    const googleError = searchParams.get("google_error");
    if (googleError) {
      toast.error(googleError, {
        autoClose: 4000,
        className: "toast-error-custom",
      });
      return;
    }

    const googleCode = searchParams.get("google_code");
    if (!googleCode) return;

    const finishGoogleLogin = async () => {
      try {
        const { data } = await API.post("/auth/google/exchange", { code: googleCode });
        localStorage.setItem("token", data.token);
        const me = await API.get("/users/me");
        setUser(me.data);
        toast.success("Google sign-in successful! Redirecting...", {
          autoClose: 1500,
          className: "toast-success-custom",
        });
        setTimeout(() => navigate("/dashboard", { replace: true }), 1200);
      } catch (err) {
        localStorage.removeItem("token");
        toast.error("Google sign-in failed. Please try again.", {
          autoClose: 4000,
          className: "toast-error-custom",
        });
      }
    };

    finishGoogleLogin();
  }, [navigate, searchParams, setUser]);

  // Refs for debounce timeouts
  const usernameTimeoutRef = useRef(null);
  const emailTimeoutRef = useRef(null);

  // Check username availability
  const checkUsernameAvailability = useCallback(async (username) => {
    if (!username || username.trim().length === 0) {
      setValidationErrors(prev => ({ ...prev, username: "" }));
      return;
    }

    const trimmedUsername = username.trim();
    console.log(`🔍 [FRONTEND] Checking username availability for: "${trimmedUsername}"`);

    setCheckingUsername(true);
    try {
      const response = await API.get(`/auth/check-username?username=${encodeURIComponent(trimmedUsername)}`);
      console.log(`🔍 [FRONTEND] Response for "${trimmedUsername}":`, response.data);

      if (response.data.available) {
        setValidationErrors(prev => ({ ...prev, username: "" }));
      } else {
        setValidationErrors(prev => ({ ...prev, username: response.data.message || "Username already taken." }));
      }
    } catch (err) {
      console.error("❌ [FRONTEND] Error checking username:", err);
      console.error("❌ [FRONTEND] Error details:", err.response?.data);
      // Don't show error on network issues, just clear the validation
      setValidationErrors(prev => ({ ...prev, username: "" }));
    } finally {
      setCheckingUsername(false);
    }
  }, []);

  // Check email availability
  const checkEmailAvailability = useCallback(async (email) => {
    if (!email || email.trim().length === 0) {
      setValidationErrors(prev => ({ ...prev, email: "" }));
      return;
    }

    // Basic email format check first
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setValidationErrors(prev => ({ ...prev, email: "Invalid email format." }));
      setCheckingEmail(false);
      return;
    }

    setCheckingEmail(false);
    setValidationErrors((prev) => ({ ...prev, email: "" }));
  }, []);

  // Debounced check functions
  const debouncedCheckUsername = useCallback((username) => {
    if (usernameTimeoutRef.current) {
      clearTimeout(usernameTimeoutRef.current);
    }
    usernameTimeoutRef.current = setTimeout(() => {
      checkUsernameAvailability(username);
    }, 500);
  }, [checkUsernameAvailability]);

  const debouncedCheckEmail = useCallback((email) => {
    if (emailTimeoutRef.current) {
      clearTimeout(emailTimeoutRef.current);
    }
    emailTimeoutRef.current = setTimeout(() => {
      checkEmailAvailability(email);
    }, 500);
  }, [checkEmailAvailability]);

  // Clear validation errors when switching tabs
  useEffect(() => {
    if (isLogin) {
      setValidationErrors({ username: "", email: "" });
      setFormData({ username: "", email: "", password: "" });
    }
  }, [isLogin]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (usernameTimeoutRef.current) {
        clearTimeout(usernameTimeoutRef.current);
      }
      if (emailTimeoutRef.current) {
        clearTimeout(emailTimeoutRef.current);
      }
    };
  }, []);

  const handleLogin = async () => {
    if (isLoading || !email || !password) return;

    setIsLoading(true);
    try {
      // Override timeout for login requests (60s for Render free tier cold starts)
      const response = await API.post("/auth/login", { email, password }, { timeout: 60000 });
      const { token, user } = response.data;

      // 1. Store JWT
      localStorage.setItem("token", token);

      // 2. Update context user
      setUser(user);

      // 3. Redirect to dashboard
      toast.success("Login successful! Redirecting...", {
        autoClose: 1500,
        className: "toast-success-custom"
      });
      setTimeout(() => navigate("/dashboard"), 1500);
    } catch (err) {
      console.error("❌ Error logging in:", err);

      // Better error messages
      let msg = "Login failed. Please try again.";
      let toastType = "error";

      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        msg = "Request timed out. The server is waking up (may take up to 60 seconds). Please wait and try again.";
        toastType = "warning";
      } else if (err.response?.status === 408) {
        msg = "Login timeout. Please try again.";
        toastType = "warning";
      } else if (err.response?.status === 400) {
        msg = err.response?.data?.message || "Invalid email or password.";
      } else if (err.response?.status === 403) {
        msg = err.response?.data?.message || "Please verify your email before logging in.";
      } else if (err.response?.status === 429) {
        msg = "Too many login attempts. Please wait a moment and try again.";
        toastType = "warning";
      } else if (err.response?.data?.message) {
        msg = err.response.data.message;
      } else if (err.message) {
        msg = err.message;
      }

      if (toastType === "warning") {
        toast.warning(msg, {
          autoClose: 5000,
          className: "toast-warning-custom"
        });
      } else {
        toast.error(msg, {
          autoClose: 4000,
          className: "toast-error-custom"
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async () => {
    if (isRegistering || !formData.username || !formData.email || !formData.password) return;
    if (formData.password.length < 8) {
      toast.error("Password must be at least 8 characters.", {
        autoClose: 4000,
        className: "toast-error-custom",
      });
      return;
    }

    setIsRegistering(true);
    try {
      await API.post("/auth/register", {
        username: formData.username,
        email: formData.email,
        password: formData.password,
      });

      toast.success("Registration successful! Check your email to verify.", {
        autoClose: 4000,
        className: "toast-success-custom"
      });
      setTimeout(() => setIsLogin(true), 3000);
    } catch (err) {
      console.error("❌ Registration failed:", err);
      const msg =
        err.response?.data?.message ||
        "Registration failed. Please try again.";
      toast.error(msg, {
        autoClose: 4000,
        className: "toast-error-custom"
      });
    } finally {
      setIsRegistering(false);
    }
  };

  const handleClearSession = () => {
    localStorage.removeItem("token");
    setUser(null);
    toast.success("Session cleared! Please try logging in again.", {
      autoClose: 3000,
      className: "toast-success-custom"
    });
  };

  const handleGoogleSignIn = () => {
    const apiUrl = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
    if (!apiUrl) {
      toast.error("Google sign-in is not configured for this environment.", {
        autoClose: 3500,
        className: "toast-error-custom",
      });
      return;
    }
    window.location.href = `${apiUrl}/api/auth/google/start`;
  };

  return (
    <div className="min-h-screen bg-paper text-ink lg:grid lg:grid-cols-[480px_1fr] xl:grid-cols-[540px_1fr]">
      <div className="flex min-h-screen flex-col lg:bg-white">
        <div className="flex min-h-screen w-full flex-col px-6 pb-6 pt-8 sm:px-10 sm:pb-8 sm:pt-12 lg:px-12 lg:pt-14">
        <img src="/Predict90Wordmark.png" alt="Predict90" className="mx-auto h-16 w-auto sm:h-20 lg:h-24" />
        <h1 className="mt-8 text-[26px] font-bold leading-tight text-ink md:text-[32px]">
          {isLogin ? "Welcome back" : "You're invited"}
        </h1>
        <p className="mt-2 text-[15px] leading-relaxed text-muted">
          {isLogin
            ? "Sign in and get your picks in before the lock."
            : "Pick every score before it locks. Exact is 5 points. Free."}
        </p>
              <div className="mt-8 flex w-full flex-col overflow-hidden max-lg:rounded-xl max-lg:border max-lg:border-line max-lg:bg-white max-lg:shadow-sm">
                <div className="relative z-10 px-6 pb-2 pt-6 lg:px-0 lg:pt-0">
                  {/* Tab Selector */}
                  <div className="grid h-12 grid-cols-2 rounded-xl bg-gray-100 p-1">
                    <button
                      className={`rounded-lg text-sm font-semibold transition ${
                        isLogin
                          ? "bg-white text-ink shadow-sm"
                          : "text-muted hover:text-ink"
                      }`}
                      onClick={() => setIsLogin(true)}
                    >
                      Sign in
                    </button>
                    <button
                      className={`rounded-lg text-sm font-semibold transition ${
                        !isLogin
                          ? "bg-white text-ink shadow-sm"
                          : "text-muted hover:text-ink"
                      }`}
                      onClick={() => setIsLogin(false)}
                    >
                      Register
                    </button>
                  </div>
                </div>

                {/* Form Content with Smooth Transition */}
                <div className="relative z-10 flex-1 px-6 py-6 lg:px-0">
                  <div className="transition-all duration-300 ease-in-out">
                    {isLogin ? (
                      <div className="space-y-5 animate-fadeIn">
                        <div>
                          <label htmlFor="login-email" className="mb-1.5 block text-[13px] font-medium text-ink">
                            Email
                          </label>
                          <input
                            id="login-email"
                            type="email"
                            placeholder="Enter your email"
                            className={`${fieldClass} border-line`}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                          />
                        </div>
                        <div>
                          <label htmlFor="login-password" className="mb-1.5 block text-[13px] font-medium text-ink">
                            Password
                          </label>
                          <input
                            id="login-password"
                            type="password"
                            placeholder="Enter your password"
                            className={`${fieldClass} border-line`}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter' && !isLoading && email && password) {
                                handleLogin();
                              }
                            }}
                          />
                        </div>
                        <button
                          onClick={handleLogin}
                          disabled={isLoading || !email || !password}
                          className={[
                            "mt-2 flex h-12 w-full items-center justify-center gap-2 rounded-xl text-[15px] font-semibold transition",
                            isLoading || !email || !password
                              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                              : "bg-brand text-white hover:bg-brand-hover",
                          ].join(" ")}
                        >
                          {isLoading ? (
                            <>
                              <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              <span>Signing in...</span>
                            </>
                          ) : (
                            "Sign In"
                          )}
                        </button>
                        <button
                          onClick={handleGoogleSignIn}
                          className="mt-3 flex h-12 w-full items-center justify-center gap-3 rounded-xl border border-line bg-white text-[15px] font-medium text-ink transition hover:bg-gray-50"
                        >
                          <svg className="h-5 w-5" viewBox="0 0 48 48" aria-hidden="true">
                            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                          </svg>
                          Continue with Google
                        </button>
                        <div className="flex items-center justify-between pt-2">
                          <a
                            href="/forgot-password"
                            className="text-sm text-gray-600 hover:text-gray-900 underline"
                          >
                            Forgot password?
                          </a>
                          <button
                            type="button"
                            onClick={handleClearSession}
                            className="text-sm text-gray-500 hover:text-gray-700 underline"
                          >
                            Having trouble?
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-5 animate-fadeIn">
                        <div>
                          <label htmlFor="reg-username" className="mb-1.5 block text-[13px] font-medium text-ink">
                            Username
                          </label>
                          <div className="relative">
                            <input
                              id="reg-username"
                              type="text"
                              placeholder="Choose a username"
                              className={[
                                fieldClass,
                                "pr-10",
                                validationErrors.username
                                  ? "border-red-500 focus:ring-red-500"
                                  : formData.username && !checkingUsername && !validationErrors.username
                                    ? "border-green-500"
                                    : "border-line",
                              ].join(" ")}
                              value={formData.username}
                              onChange={(e) => {
                                setFormData({ ...formData, username: e.target.value });
                                setValidationErrors(prev => ({ ...prev, username: "" }));
                                debouncedCheckUsername(e.target.value);
                              }}
                              onBlur={() => {
                                if (formData.username.trim()) {
                                  checkUsernameAvailability(formData.username);
                                }
                              }}
                            />
                            {checkingUsername && (
                              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                <svg className="animate-spin h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                              </div>
                            )}
                            {!checkingUsername && formData.username && !validationErrors.username && (
                              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                <svg className="h-5 w-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                            )}
                          </div>
                          {validationErrors.username && (
                            <p className="mt-1.5 text-sm text-red-600">{validationErrors.username}</p>
                          )}
                        </div>
                        <div>
                          <label htmlFor="reg-email" className="mb-1.5 block text-[13px] font-medium text-ink">
                            Email
                          </label>
                          <div className="relative">
                            <input
                              id="reg-email"
                              type="email"
                              placeholder="Enter your email"
                              className={[
                                fieldClass,
                                "pr-10",
                                validationErrors.email
                                  ? "border-red-500 focus:ring-red-500"
                                  : formData.email && !checkingEmail && !validationErrors.email
                                    ? "border-green-500"
                                    : "border-line",
                              ].join(" ")}
                              value={formData.email}
                              onChange={(e) => {
                                setFormData({ ...formData, email: e.target.value });
                                setValidationErrors(prev => ({ ...prev, email: "" }));
                                debouncedCheckEmail(e.target.value);
                              }}
                              onBlur={() => {
                                if (formData.email.trim()) {
                                  checkEmailAvailability(formData.email);
                                }
                              }}
                            />
                            {checkingEmail && (
                              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                <svg className="animate-spin h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                              </div>
                            )}
                            {!checkingEmail && formData.email && !validationErrors.email && (
                              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                <svg className="h-5 w-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                            )}
                          </div>
                          {validationErrors.email && (
                            <p className="mt-1.5 text-sm text-red-600">{validationErrors.email}</p>
                          )}
                        </div>
                        <div>
                          <label htmlFor="reg-password" className="mb-1.5 block text-[13px] font-medium text-ink">
                            Password
                          </label>
                          <input
                            id="reg-password"
                            type="password"
                            placeholder="Create a password (min. 8 characters)"
                            className={`${fieldClass} border-line`}
                            value={formData.password}
                            onChange={(e) =>
                              setFormData({ ...formData, password: e.target.value })
                            }
                            onKeyPress={(e) => {
                              if (e.key === 'Enter' && !isRegistering && formData.username && formData.email && formData.password) {
                                handleRegister();
                              }
                            }}
                          />
                        </div>

                        <button
                          onClick={handleRegister}
                          disabled={isRegistering || !formData.username || !formData.email || !formData.password || validationErrors.username || validationErrors.email || checkingUsername || checkingEmail}
                          className={[
                            "mt-2 flex h-12 w-full items-center justify-center gap-2 rounded-xl text-[15px] font-semibold transition",
                            isRegistering || !formData.username || !formData.email || !formData.password || validationErrors.username || validationErrors.email || checkingUsername || checkingEmail
                              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                              : "bg-brand text-white hover:bg-brand-hover",
                          ].join(" ")}
                        >
                          {isRegistering ? (
                            <>
                              <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              <span>Registering...</span>
                            </>
                          ) : (
                            "Create Account"
                          )}
                        </button>
                        <ScoringChips className="text-center pt-1" />
                        <p className="text-xs text-gray-500 text-center pt-2">
                          By registering, you agree to receive verification emails. Make sure to check your inbox!
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

        <div className="mt-auto pt-4 sm:pt-10">
          <GoalShot className="max-h-24 sm:max-h-none" />
        </div>

        <ToastContainer
          position="top-center"
          autoClose={4000}
          hideProgressBar={false}
          newestOnTop={true}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
          className="toast-container-custom"
          toastClassName="toast-custom"
          bodyClassName="toast-body-custom"
          progressClassName="toast-progress-custom"
        />
        </div>
      </div>

      <aside className="relative hidden lg:block">
        <div className="sticky top-0 h-screen overflow-hidden">
          <img
            src="/Login_placeholder.jpg"
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/15" />
          <div className="absolute inset-x-0 bottom-0 p-10 xl:p-14 text-white">
            <h2 className="max-w-lg text-[40px] font-bold leading-[1.1]">
              Call the score. Beat your mates.
            </h2>
            <p className="mt-4 max-w-md text-[17px] leading-relaxed text-white/85">
              Premier League, Champions League, and the rest. 5 for exact, 3 for the right goal difference or a correct draw.
            </p>
          </div>
        </div>
      </aside>
    </div>
  );
}

export default AuthPage;
